import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MeetingTypeApiService } from '../../../services/meeting-type-api.service';
import { MeetingTypeAvailabilityComponent } from '../meeting-type-availability/meeting-type-availability.component';
import {
  MeetingTypeRequest,
  MeetingTypeResponse,
} from '../../../models/meeting.model';
import { MEETING_TYPE_ICONS, meetingTypeIconPaths } from '../meeting-type-icons';

@Component({
  selector: 'app-meeting-types-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, MeetingTypeAvailabilityComponent],
  templateUrl: './meeting-types-form.component.html',
})
export class MeetingTypesFormComponent implements OnInit {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<MeetingTypeResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  typeId: number | null = null;
  currentSortOrder: number | null = null;
  currentIsActive = true;
  private originalName = '';

  // INC 33 — selector visual de íconos (set consistente con el resto del sistema)
  readonly iconOptions = MEETING_TYPE_ICONS;

  /** Paths SVG del ícono seleccionado (o de cualquier clave) para render. */
  iconPaths(key?: string | null): string[] {
    return meetingTypeIconPaths(key);
  }

  /** Marca/desmarca el ícono en el control `icon` (toggle si se vuelve a pulsar). */
  selectIcon(key: string): void {
    const current = this.form.get('icon')?.value;
    this.form.patchValue({ icon: current === key ? '' : key });
    this.form.get('icon')?.markAsDirty();
  }

  isIconSelected(key: string): boolean {
    return this.form.get('icon')?.value === key;
  }

  constructor(
    private fb: FormBuilder,
    private api: MeetingTypeApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // En drawer mode el wrapper invoca reset()/loadType() al abrir.
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(150)]],
      description: ['', [Validators.maxLength(500)]],
      color: ['#3b82f6'],
      icon: ['', [Validators.maxLength(50)]],
      defaultDurationMinutes: [30],
      requiresLocation: [false],
      requiresUrl: [false],
      sortOrder: [0],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.typeId = null;
    this.currentSortOrder = null;
    this.currentIsActive = true;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadType(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.typeId = id;
    this.api.getById(id, this.businessId).subscribe({
      next: (item) => {
        this.currentSortOrder = item.sortOrder ?? null;
        this.currentIsActive = item.isActive ?? true;
        this.form.patchValue({
          name: item.name,
          label: item.label,
          description: item.description || '',
          color: item.color || '#3b82f6',
          icon: item.icon || '',
          defaultDurationMinutes: item.defaultDurationMinutes ?? 30,
          requiresLocation: !!item.requiresLocation,
          requiresUrl: !!item.requiresUrl,
          sortOrder: item.sortOrder ?? 0,
          isActive: item.isActive ?? true,
        });
        this.originalName = item.label;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el tipo';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: MeetingTypeRequest = {
      businessId: this.businessId,
      name: v.name.trim(),
      label: v.label.trim(),
      description: v.description?.trim() || undefined,
      color: v.color || undefined,
      icon: v.icon?.trim() || undefined,
      defaultDurationMinutes: v.defaultDurationMinutes ?? undefined,
      requiresLocation: !!v.requiresLocation,
      requiresUrl: !!v.requiresUrl,
      sortOrder: v.sortOrder ?? 0,
      isActive: !!v.isActive,
    };

    const req$ = this.isEditing && this.typeId
      ? this.api.update(this.typeId, body)
      : this.api.create(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Tipo de reunión',
            this.originalName !== v.label.trim()
              ? `Se actualizó "${this.originalName}" → "${v.label.trim()}".`
              : `Se actualizó "${v.label.trim()}" correctamente.`);
        } else {
          this.toast.created('Tipo de reunión', `Se creó "${v.label.trim()}" correctamente.`);
        }
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
